/* ────────────────────────────────────────────────────────────────
   useSaleStore.ts   •  Zustand store for active Sale
   Depends on sale-schemas.ts
   ──────────────────────────────────────────────────────────────── */

import { produce } from 'immer';
import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';
import { devtools } from 'zustand/middleware';
import {
  SaleData,
  SaleStore,
  SalesItem,
  SaleSchema,
  FailedPaymentTx,
  SalesItemSchema,
  PendingPaymentTx,
  ActionStatusEnum,
  SourceStatusEnum,
  SalesChannelEnum,
  CompletedPaymentTx,
  getSaleOrderDetails,
  withSaleOrderDetails,
  type SaleOrderDetails,
  mergeSaleOrderDetails,
  calculateTotalsForSale,
} from '@am3nd-org/pointcraft-types';

import { customNanoId } from 'src/utils/msc';
import { saleOrderDetailsSync } from 'src/utils/sales/order-details-sync';
import {
  type EditableSaleDraft,
  createEditableSaleSnapshot,
} from 'src/utils/sales/editable-sale-snapshot';

import { useDeviceSyncStore } from '../DeviceSync';
import { isUuid, useDeviceContextStore } from '../DeviceContext';
import { selectCurrentSaleEmployee } from '../DeviceSync/selectors';

function resolveSaleBusinessLocationFromStores(): { id: string; name: string } {
  const deviceContext = useDeviceContextStore.getState();

  return {
    id: isUuid(deviceContext.businessLocationId) ? deviceContext.businessLocationId : '',
    name: deviceContext.deviceName || 'Unknown',
  };
}

/**
 * Extended SaleStore interface with payment flow state
 * Adds payment processing state and error handling to base SaleStore
 */
export type ExtendedSaleStore = SaleStore & {
  /** Payment processing state */
  paymentProcessing: boolean;

  /** Payment error information */
  paymentError: {
    code: string;
    message: string;
  } | null;

  /**
   * When true, "Remove Fees" was explicitly checked by the cashier.
   * recalculateTotals respects this flag and skips re-syncing billingControls
   * from business settings, preserving clientPaysFees=false and taxEnabled=false.
   */
  removeFeesOverride: boolean;

  /**
   * Granular per-field overrides set by the cashier from the toolbar toggles.
   * null = follow business defaults; boolean = cashier-set manual value.
   * These take precedence over business defaults but are overridden by removeFeesOverride.
   */
  taxEnabledOverride: boolean | null;
  clientPaysFeesOverride: boolean | null;

  /** Set payment processing state */
  setPaymentProcessing: (isProcessing: boolean) => void;

  /** Set payment error */
  setPaymentError: (error: { code: string; message: string } | null) => void;

  /** Clear payment error */
  clearPaymentError: () => void;

  /** Toggle the "Remove Fees" override. Immediately recalculates totals. */
  setRemoveFeesOverride: (value: boolean) => void;

  /** Manually override tax enabled state. Pass null to revert to business defaults. */
  setSaleTaxEnabled: (value: boolean | null) => void;

  /** Manually override whether customer pays fees. Pass null to revert to business defaults. */
  setSaleClientPaysFees: (value: boolean | null) => void;

  /** Add a completed payment */
  addCompletedPayment: (payment: CompletedPaymentTx) => void;

  /** Update payment status (move between pending/completed/failed) */
  updatePaymentStatus: (paymentId: string, status: ActionStatusEnum) => void;

  /** Add a failed payment */
  addFailedPayment: (payment: FailedPaymentTx) => void;

  /** Replace all sale line-items wholesale (e.g. Quick Pay). Recalculates totals. */
  setSaleItems: (items: SalesItem[]) => void;

  /** Atomically restore a validated, non-financial active-sale draft. */
  hydratePersistedSaleDraft: (draft: EditableSaleDraft) => void;

  /** Re-sync employee + business location from the current user/device (after preload). */
  updateSaleBusinessData: () => void;

  /**
   * Replace a cancelled sale with a fresh local sale. When requested, only
   * schema-approved cart-line data survives; sale, line, and payment identity
   * never carries into the next checkout attempt.
   */
  replaceSaleAfterCancellation: () => void;

  /** Apply an already allocated replacement in one observable store write. */
  replaceSaleAfterExit: (sale: SaleData) => void;

  /** Merge editable metadata without changing the payment journal or totals. */
  setOrderDetails: (details: SaleOrderDetails) => void;
  /** @deprecated Use setOrderDetails({ orderNumber, requiresOrderProcessing }). */
  setSaleOrder: (order: { number: string; requiresProcessing: boolean } | null) => void;
};

/* ------------------------------------------------------------------ */
/* helper: create blank sale                                           */
/* ------------------------------------------------------------------ */
const createEmptySale = (): SaleData => {
  const userDataStore = useDeviceSyncStore.getState();
  const businessLocation = resolveSaleBusinessLocationFromStores();

  const timestamp = new Date();
  const schemaCheck: SaleData = {
    id: uuidv4(),
    items: [],
    salesMeta: {
      shortIdentifier: customNanoId(),

      // Billing controls from business settings
      billingControls: {
        taxEnabled: !!userDataStore.businessData?.financialDefaults.taxRate,
        processingFeesEnabled: true,
        clientPaysFees:
          (userDataStore.businessData?.financialDefaults.processingConfig.customerSplits?.rate ??
            0) > 0 ||
          (userDataStore.businessData?.financialDefaults.processingConfig.customerSplits?.fixed ??
            0) > 0,
      },

      // Invoice controls - null for POS sales
      invoiceControls: null,

      // Notes structure
      notes: {
        client: null,
        merchant: null,
        platform: null,
      },

      // Sale type - default to POS
      salesChannel: SalesChannelEnum.POS,

      // Order - null initially
      order: null,

      // QuickBooks sync status
      syncedToQuickBooks: false,

      employee: selectCurrentSaleEmployee(userDataStore),
      customer: null,
      businessLocation,
      createdAt: timestamp,
      updatedAt: timestamp,
    },
    status: {
      hasBeenCreated: false,
      isCompleted: false,
      isReadyToPay: false,
      currentSaleStatus: SourceStatusEnum.UNPAID,
    },
    financialInfo: {
      total: 0,
      cost: 0,
      subTotal: 0,
      tip: 0,
      balanceDue: 0,
      processingFees: 0,
      merchantProcessingFees: 0,
      adjustment: 0,
      taxes: 0,
      discount: 0,
      shippingCharges: 0,
      // Snapshot of processing config for audit trail
      processingConfig: userDataStore.businessData?.financialDefaults.processingConfig || {
        rate: 0,
        flatFeeRules: [],
        flatFeeExemptions: [],
        customerSplits: { rate: 0, fixed: 0 },
      },
    },
    paymentInfo: {
      pendingPayments: [],
      completedPayments: [],
      failedPayments: [],
      refundedPayments: [],
      totalAmount: 0,
    },
  };

  return SaleSchema.parse(schemaCheck);
};

/** Allocate once, then persist this exact replacement before applying it. */
export function createSaleAfterExit(source: SaleData, keepItems: boolean): SaleData {
  const sale = createEmptySale();
  sale.items = keepItems
    ? source.items.map((item) => SalesItemSchema.parse({ ...item, id: uuidv4() }))
    : [];
  if (sale.items.length === 0) return sale;
  const defaults = useDeviceSyncStore.getState().businessData?.financialDefaults;
  const totals = calculateTotalsForSale({
    items: sale.items,
    adjustment: 0,
    clientPaysFees: sale.salesMeta.billingControls?.clientPaysFees ?? false,
    pricingConfig: sale.financialInfo.processingConfig,
    taxRate: sale.salesMeta.billingControls?.taxEnabled ? (defaults?.taxRate ?? 0) : 0,
  });
  sale.financialInfo = { ...sale.financialInfo, ...totals, balanceDue: totals.total };
  return SaleSchema.parse(sale);
}

/* ------------------------------------------------------------------ */
/* Zustand store definition                                            */
/* ------------------------------------------------------------------ */

export const useSaleStore = create<ExtendedSaleStore>()(
  devtools(
    (set, get) => ({
      sale: createEmptySale() as SaleData,

      /* Payment Flow State */
      paymentProcessing: false,
      paymentError: null,
      removeFeesOverride: false,
      taxEnabledOverride: null,
      clientPaysFeesOverride: null,

      /* 1 ▸ new blank sale */
      createNewSale: () =>
        set(
          {
            sale: createEmptySale(),
            paymentError: null,
            paymentProcessing: false,
            removeFeesOverride: false,
            taxEnabledOverride: null,
            clientPaysFeesOverride: null,
          },
          false,
          'sale/createNewSale'
        ),

      /* 1b ▸ fresh sale after cancellation, always preserving the cart lines */
      replaceSaleAfterCancellation: () => {
        get().replaceSaleAfterExit(createSaleAfterExit(get().sale, true));
      },

      replaceSaleAfterExit: (sale) =>
        set(
          {
            sale: createEditableSaleSnapshot(sale),
            paymentError: null,
            paymentProcessing: false,
            removeFeesOverride: false,
            taxEnabledOverride: null,
            clientPaysFeesOverride: null,
          },
          false,
          'sale/replaceAfterExit'
        ),

      /* 2 ▸ overwrite current sale */
      setCurrentSale: (sale) =>
        set({ sale: withSaleOrderDetails(sale) }, false, 'sale/setCurrentSale'),

      /* 2b ▸ restore an active draft before POS/payment page effects mount */
      hydratePersistedSaleDraft: (draft) => {
        set(
          {
            sale: createEditableSaleSnapshot(draft.sale),
            removeFeesOverride: draft.overrides.removeFeesOverride,
            taxEnabledOverride: draft.overrides.taxEnabledOverride,
            clientPaysFeesOverride: draft.overrides.clientPaysFeesOverride,
            paymentProcessing: false,
            paymentError: null,
          },
          false,
          'sale/hydratePersistedSaleDraft'
        );
        get().updateSaleBusinessData();
        get().recalculateTotals();
      },

      /* 3 ▸ add or patch line-item */
      updateOrAddItem: (identifier, partial) =>
        set(
          produce<SaleStore>((state) => {
            const idx = state.sale.items.findIndex((it: any) => it.identifier === identifier);
            if (idx >= 0) {
              state.sale.items[idx] = { ...state.sale.items[idx], ...partial };
            } else {
              state.sale.items.push({
                id: uuidv4(),
                identifier,
                name: partial.name ?? 'Unnamed Item',
                price: partial.price ?? 0,
                cost: partial.cost ?? 0,
                quantity: partial.quantity ?? 1,
                linkedProduct: partial.linkedProduct ?? null,
                linkedMobileService: null,
                productType: partial.productType ?? null,
                metadata: {
                  isTaxable: partial.metadata?.isTaxable ?? true,
                  ebtEligible: partial.metadata?.ebtEligible ?? false,
                  oneTouchGroupId: partial.metadata?.oneTouchGroupId,
                  oneTouchGroupName: partial.metadata?.oneTouchGroupName,
                  specialInstructions: partial.metadata?.specialInstructions,
                  selectedModifierData: partial.metadata?.selectedModifierData ?? [],
                },
              });
            }
          }),
          false,
          'sale/updateOrAddItem'
        ),
      removeItem: (identifier) =>
        set(
          produce<SaleStore>((state) => {
            state.sale.items = state.sale.items.filter((it: any) => it.identifier !== identifier);
          }),
          false,
          'sale/removeItem'
        ),

      /* 3b ▸ replace all line-items wholesale (Quick Pay) */
      setSaleItems: (items) =>
        set(
          produce<SaleStore>((state) => {
            state.sale.items = items;
          }),
          false,
          'sale/setSaleItems'
        ),

      /* 4 ▸ base sale field updater */
      updateBaseSaleFields: (partial) =>
        set(
          produce<SaleStore>((state) => {
            if (partial.id !== undefined) {
              state.sale.id = partial.id;
            }
          }),
          false,
          'sale/updateBaseSaleFields'
        ),

      /* 5 ▸ status / meta setters */
      setStatus: (partial) =>
        set(
          produce<SaleStore>((state) => {
            state.sale.status = { ...state.sale.status, ...partial };
          }),
          false,
          'sale/setStatus'
        ),
      setMetaInfo: (partial) =>
        set(
          produce<SaleStore>((state) => {
            state.sale.salesMeta = { ...state.sale.salesMeta, ...partial, updatedAt: new Date() };
          }),
          false,
          'sale/setMetaInfo'
        ),

      /* ▸ re-sync employee + business location from the current user/device */
      updateSaleBusinessData: () =>
        set(
          produce<SaleStore>((state) => {
            const userStore = useDeviceSyncStore.getState();
            const businessLocation = resolveSaleBusinessLocationFromStores();
            state.sale.salesMeta.employee = selectCurrentSaleEmployee(userStore);
            state.sale.salesMeta.businessLocation = businessLocation;
            state.sale.salesMeta.updatedAt = new Date();
          }),
          false,
          'sale/updateSaleBusinessData'
        ),

      setOrderDetails: (details) => {
        const businessId = useDeviceSyncStore.getState().businessData?.id;
        if (businessId)
          saleOrderDetailsSync.enqueue({ businessId, sourceCheckoutId: get().sale.id }, details);
        const saved = businessId
          ? saleOrderDetailsSync.get({ businessId, sourceCheckoutId: get().sale.id })?.details
          : undefined;
        set(
          produce<ExtendedSaleStore>((state) => {
            state.sale.orderDetails = mergeSaleOrderDetails(
              getSaleOrderDetails(state.sale),
              saved ?? details
            );
            state.sale.salesMeta.updatedAt = new Date();
          }),
          false,
          'sale/setOrderDetails'
        );
      },

      // Compatibility entry point for integrations still using the old setter.
      setSaleOrder: (order) =>
        get().setOrderDetails({
          orderNumber: order?.number ?? null,
          requiresOrderProcessing: order?.requiresProcessing ?? null,
        }),

      /* 6 ▸ financial info setter */
      setFinancialInfo: (partial) =>
        set(
          produce<SaleStore>((state) => {
            state.sale.financialInfo = { ...state.sale.financialInfo, ...partial };
          }),
          false,
          'sale/setFinancialInfo'
        ),

      /* 7 ▸ payment info setter */
      setPaymentInfo: (partial) =>
        set(
          produce<SaleStore>((state) => {
            state.sale.paymentInfo = { ...state.sale.paymentInfo, ...partial };
            // Recalculate totalAmount if completed payments changed
            if (partial.completedPayments) {
              state.sale.paymentInfo.totalAmount = partial.completedPayments.reduce(
                (sum: number, tx: CompletedPaymentTx) => sum + tx.amount,
                0
              );
            }
          }),
          false,
          'sale/setPaymentInfo'
        ),

      /* 8 ▸ pending payment (legacy helper) */
      addPendingPayment: (tx) =>
        set(
          produce<ExtendedSaleStore>((state) => {
            state.sale.paymentInfo.pendingPayments.push(tx);
          }),
          false,
          'sale/addPendingPayment'
        ),

      /* ──── PAYMENT FLOW METHODS ──── */

      /* Set payment processing state */
      setPaymentProcessing: (isProcessing) =>
        set({ paymentProcessing: isProcessing }, false, 'sale/setPaymentProcessing'),

      /* Set payment error */
      setPaymentError: (error) => set({ paymentError: error }, false, 'sale/setPaymentError'),

      /* Clear payment error */
      clearPaymentError: () => set({ paymentError: null }, false, 'sale/clearPaymentError'),

      /* Toggle "Remove Fees" override — recalculateTotals will honour the flag */
      setRemoveFeesOverride: (value: boolean) => {
        set(
          produce<ExtendedSaleStore>((state) => {
            state.removeFeesOverride = value;
          }),
          false,
          'sale/setRemoveFeesOverride'
        );
        get().recalculateTotals();
      },

      setSaleTaxEnabled: (value) => {
        set({ taxEnabledOverride: value }, false, 'sale/setSaleTaxEnabled');
        get().recalculateTotals();
      },

      setSaleClientPaysFees: (value) => {
        set({ clientPaysFeesOverride: value }, false, 'sale/setSaleClientPaysFees');
        get().recalculateTotals();
      },

      /* Add completed payment */
      addCompletedPayment: (payment) =>
        set(
          produce<ExtendedSaleStore>((state) => {
            // Remove from pending if it exists
            state.sale.paymentInfo.pendingPayments = state.sale.paymentInfo.pendingPayments.filter(
              (p: PendingPaymentTx) => p.id !== payment.id
            );

            // Add to completed
            state.sale.paymentInfo.completedPayments.push(payment);

            // Recalculate total amount
            state.sale.paymentInfo.totalAmount = state.sale.paymentInfo.completedPayments.reduce(
              (sum: number, tx: CompletedPaymentTx) => sum + tx.amount,
              0
            );
          }),
          false,
          'sale/addCompletedPayment'
        ),

      /* Update payment status */
      updatePaymentStatus: (paymentId, status) =>
        set(
          produce<ExtendedSaleStore>((state) => {
            // Find payment in any of the arrays
            const pendingIdx = state.sale.paymentInfo.pendingPayments.findIndex(
              (p: PendingPaymentTx) => p.id === paymentId
            );
            const completedIdx = state.sale.paymentInfo.completedPayments.findIndex(
              (p: CompletedPaymentTx) => p.id === paymentId
            );
            const failedIdx = state.sale.paymentInfo.failedPayments.findIndex(
              (p: FailedPaymentTx) => p.id === paymentId
            );

            let basePayment: any = null;

            // Get the payment and remove from current array
            if (pendingIdx >= 0) {
              basePayment = { ...state.sale.paymentInfo.pendingPayments[pendingIdx] };
              state.sale.paymentInfo.pendingPayments.splice(pendingIdx, 1);
            } else if (completedIdx >= 0) {
              basePayment = { ...state.sale.paymentInfo.completedPayments[completedIdx] };
              state.sale.paymentInfo.completedPayments.splice(completedIdx, 1);
            } else if (failedIdx >= 0) {
              basePayment = { ...state.sale.paymentInfo.failedPayments[failedIdx] };
              // Remove failureDetails when moving from failed to another status
              delete basePayment.failureDetails;
              state.sale.paymentInfo.failedPayments.splice(failedIdx, 1);
            }

            if (!basePayment) return;

            // Create new payment object with appropriate type based on status
            if (status === ActionStatusEnum.SUCCESS) {
              const completedPayment: CompletedPaymentTx = {
                ...basePayment,
                status: ActionStatusEnum.SUCCESS,
              };
              state.sale.paymentInfo.completedPayments.push(completedPayment);
            } else if (status === ActionStatusEnum.FAILED) {
              const failedPayment: FailedPaymentTx = {
                ...basePayment,
                status: ActionStatusEnum.FAILED,
                failureDetails: basePayment.failureDetails || {
                  failureReason: 'Payment failed',
                  failureCode: 'PAYMENT_FAILED',
                  failedAt: new Date(),
                  retryable: true,
                  processorMessage: null,
                },
              };
              state.sale.paymentInfo.failedPayments.push(failedPayment);
            } else if (status === ActionStatusEnum.PENDING) {
              const pendingPayment: PendingPaymentTx = {
                ...basePayment,
                status: ActionStatusEnum.PENDING,
              };
              state.sale.paymentInfo.pendingPayments.push(pendingPayment);
            }

            // Recalculate total amount
            state.sale.paymentInfo.totalAmount = state.sale.paymentInfo.completedPayments.reduce(
              (sum: number, tx: CompletedPaymentTx) => sum + tx.amount,
              0
            );
          }),
          false,
          'sale/updatePaymentStatus'
        ),

      /* Add failed payment */
      addFailedPayment: (payment) =>
        set(
          produce<ExtendedSaleStore>((state) => {
            // Remove from pending if it exists
            state.sale.paymentInfo.pendingPayments = state.sale.paymentInfo.pendingPayments.filter(
              (p: PendingPaymentTx) => p.id !== payment.id
            );

            // Add to failed
            state.sale.paymentInfo.failedPayments.push(payment);
          }),
          false,
          'sale/addFailedPayment'
        ),

      /* 9 ▸ totals recomputation */
      recalculateTotals: () =>
        set(
          produce<ExtendedSaleStore>((state) => {
            const { sale } = state;

            // Always use the latest processing config from userDataStore
            const currentProcessingConfig =
              useDeviceSyncStore.getState().businessData?.financialDefaults?.processingConfig;
            if (currentProcessingConfig) {
              sale.financialInfo.processingConfig = currentProcessingConfig;

              // Sync clientPaysFees: removeFeesOverride wins, then cashier manual override, then business defaults.
              if (sale.salesMeta.billingControls) {
                if (state.removeFeesOverride) {
                  sale.salesMeta.billingControls.clientPaysFees = false;
                } else if (state.clientPaysFeesOverride !== null) {
                  sale.salesMeta.billingControls.clientPaysFees = state.clientPaysFeesOverride;
                } else {
                  const { customerSplits } = currentProcessingConfig;
                  sale.salesMeta.billingControls.clientPaysFees =
                    (customerSplits?.rate ?? 0) > 0 || (customerSplits?.fixed ?? 0) > 0;
                }
              }
            }

            // Re-sync taxEnabled: removeFeesOverride wins, then cashier manual override, then business defaults.
            const currentTaxRate =
              useDeviceSyncStore.getState().businessData?.financialDefaults?.taxRate ?? 0;
            if (sale.salesMeta.billingControls) {
              if (state.removeFeesOverride) {
                sale.salesMeta.billingControls.taxEnabled = false;
              } else if (state.taxEnabledOverride !== null) {
                sale.salesMeta.billingControls.taxEnabled = state.taxEnabledOverride;
              } else {
                sale.salesMeta.billingControls.taxEnabled = currentTaxRate > 0;
              }
            }

            const { billingControls } = sale.salesMeta;

            /* external business-logic calc — pass taxRate: 0 when taxes are disabled so
               calc.total is consistent with the taxes: 0 we write below. */
            const calc = calculateTotalsForSale({
              items: sale.items,
              adjustment: sale.financialInfo.adjustment,
              clientPaysFees: billingControls?.clientPaysFees ?? false,
              pricingConfig:
                sale.financialInfo.processingConfig ||
                useDeviceSyncStore.getState().businessData?.financialDefaults?.processingConfig,
              taxRate: billingControls?.taxEnabled ? currentTaxRate : 0,
            });

            /* paid so far */
            const paid = sale.paymentInfo.completedPayments.reduce(
              (sum: number, tx: CompletedPaymentTx) => sum + tx.amount,
              0
            );

            sale.financialInfo = {
              total: calc.total,
              cost: calc.cost,
              subTotal: calc.subTotal,
              tip: sale.financialInfo.tip,
              processingFees: billingControls?.processingFeesEnabled ? calc.processingFees : 0,
              merchantProcessingFees: billingControls?.processingFeesEnabled
                ? calc.merchantProcessingFees
                : 0,
              adjustment: sale.financialInfo.adjustment,
              taxes: billingControls?.taxEnabled ? calc.taxes : 0,
              discount: sale.financialInfo.discount,
              shippingCharges: sale.financialInfo.shippingCharges,
              processingConfig: sale.financialInfo.processingConfig,
              balanceDue: Math.max(0, calc.total - paid),
            };

            // Update totalAmount in paymentInfo
            sale.paymentInfo.totalAmount = paid;
          }),
          false,
          'sale/recalculateTotals'
        ),
    }),
    { name: 'SaleStore' }
  )
);

/* ------------------------------------------------------------------ */
/* auto-recalc after every mutating action --------------------------- */
const { getState } = useSaleStore;

// wrap the core mutators so they end with recalculateTotals()
['updateOrAddItem', 'removeItem', 'setSaleItems', 'setFinancialInfo', 'setMetaInfo'].forEach(
  (fn) => {
    // @ts-ignore – dynamic
    const orig = getState()[fn];
    // @ts-ignore
    useSaleStore.setState((s) => ({
      ...s,
      [fn]: (...args: any[]) => {
        // @ts-ignore
        orig(...args);
        getState().recalculateTotals();
      },
    }));
  }
);
